// Account state provider — boot-probes GET /api/account/me and exposes
// { status: 'loading' | 'authed' | 'guest', customer } to descendants.
//
// Status transitions are strictly driven by the fetch outcome:
//   loading → authed        (me 200 with customer)
//   loading → guest         (me 401, network reject, or 200 with no customer)
// NO timeout-driven 'guest' fallback — that raced /me on slow networks and
// caused authed users to be bounced back to /account/login (see PROMPT
// FIX-POSTOTP-RETURN, mechanism A).

import { createContext, useContext, useEffect, useState } from 'react';
import { API_BASE } from '../lib/apiBase';

const AccountContext = createContext({
        status: 'loading',
        customer: null,
        refresh: () => {},
});

const BACKEND = API_BASE;

async function fetchMe(signal) {
        const res = await fetch(`${BACKEND}/api/account/me`, {
                credentials: 'include',
                signal,
        });
        if (!res.ok) return { customer: null, trigger: 'me_401' };
        const data = await res.json();
        if (data && data.ok && data.customer) {
                return { customer: data.customer, trigger: 'me_ok' };
        }
        return { customer: null, trigger: 'me_no_customer' };
}

function logStatus(from, to, trigger) {
        try {
                // Structured single-line log; picked up by both console and
                // any wired-up logdrain. Avoid PII — no customer payload.
                // eslint-disable-next-line no-console
                console.info(
                        JSON.stringify({ stage: 'account_status', from, to, trigger })
                );
        } catch (_e) {
                /* noop */
        }
}

export function AccountProvider({ children }) {
        const [status, setStatus] = useState('loading');
        const [customer, setCustomer] = useState(null);

        const load = () => {
                const controller = new AbortController();
                fetchMe(controller.signal)
                        .then(({ customer: cust, trigger }) => {
                                const next = cust ? 'authed' : 'guest';
                                setCustomer(cust || null);
                                setStatus((prev) => {
                                        logStatus(prev, next, trigger);
                                        return next;
                                });
                        })
                        .catch((err) => {
                                if (err && err.name === 'AbortError') return;
                                setCustomer(null);
                                setStatus((prev) => {
                                        logStatus(prev, 'guest', 'me_err');
                                        return 'guest';
                                });
                        });
                return () => controller.abort();
        };

        useEffect(() => {
                const stop = load();
                return stop;
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const refresh = () => {
                setStatus((prev) => {
                        logStatus(prev, 'loading', 'refresh');
                        return 'loading';
                });
                load();
        };

        return (
                <AccountContext.Provider value={{ status, customer, refresh }}>
                        {children}
                </AccountContext.Provider>
        );
}

export function useAccount() {
        return useContext(AccountContext);
}
